import { ExternalCheckScheduleDTO } from 'models/dto/integrations/externalCheckScheduleDTO';
import { IntegrationConnectionDTO } from 'models/dto/integrations/integrationConnectionDTO';
import { IntegrationDTO } from 'models/dto/integrations/integrationDTO';
import { IntegrationGroupDTO } from 'models/dto/integrations/integrationGroupDTO';
import { IntegrationResultDTO } from 'models/dto/integrations/integrationResultDTO';
import { IntegrationRunDTO } from 'models/dto/integrations/integrationRunDTO';
import { IntegrationRunStatsDTO } from 'models/dto/integrations/integrationRunStatsDTO';
import { IntegrationTypeDTO } from 'models/dto/integrations/integrationTypeDTO';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import { ExternalCheckSchedule, ExternalCheckScheduleState } from 'models/integrations/externalCheckSchedule';
import { Integration } from 'models/integrations/integration';
import { IntegrationCategories } from 'models/integrations/integrationCategories';
import { IntegrationChecks } from 'models/integrations/integrationCheckHandler';
import { IntegrationConfigBase } from 'models/integrations/integrationConfigBase';
import { IntegrationConnection } from 'models/integrations/integrationConnection';
import { IntegrationConnectionTypes } from 'models/integrations/integrationConnectionHandler';
import { IntegrationGroup } from 'models/integrations/integrationGroup';
import { IntegrationResult } from 'models/integrations/integrationResult';
import { IntegrationRun } from 'models/integrations/integrationRun';
import { IntegrationRunStats } from 'models/integrations/integrationRunStats';
import { IntegrationType } from 'models/integrations/integrationType';
import KPI from 'models/kpi/kpi';
import RecurringPattern from 'models/recurringPattern';
import { fromApiDateTimeOptional, fromApiDateTimeRequired } from 'utils/datetime';
import { mapFromKPI, mapFromKPIDataContext, mapFromKPIDataPoint, mapToKPI } from './kpiMapping';
import { mapFromTask } from './taskMapping';

export const mapFromIntegrations = (integrationDtos: IntegrationDTO[], cache: GlobalDataCache): Integration[] => {
  return integrationDtos.map((dto) => mapFromIntegration(dto, cache));
};

export const mapFromIntegration = (integrationDto: IntegrationDTO, cache: GlobalDataCache): Integration => {
  const integration = new Integration();
  integration.integrationId = integrationDto.integrationId;
  integration.integrationTypeId = integrationDto.integrationTypeId;
  integration.state = integrationDto.state;
  integration.config = integrationDto.config ? JSON.parse(integrationDto.config) : new IntegrationConfigBase();
  integration.integrationConnectionId = integrationDto.integrationConnectionId ?? undefined;
  integration.connection = integrationDto.connection
    ? mapFromIntegrationConnection(integrationDto.connection)
    : undefined;
  integration.kpiId = integrationDto.kpiId;
  integration.ownerId = integrationDto.ownerId;
  integration.ownerRoleId = integrationDto.ownerRoleId;
  integration.kpi = mapFromKPI(integrationDto.kpi, cache);
  integration.integrationType = integrationDto.integrationType
    ? mapFromIntegrationType(integrationDto.integrationType, cache)
    : undefined;
  integration.runs = integrationDto.runs?.map((runDto) => mapFromIntegrationRun(runDto, integration.kpi, cache)) ?? [];
  integration.templateId = integrationDto.templateId;
  integration.template = integrationDto.template ? mapFromTask(integrationDto.template, cache) : undefined;
  integration.integrationGroupId = integrationDto.integrationGroupId;
  integration.integrationGroup = mapFromIntegrationGroup(integrationDto.integrationGroup);
  integration.externalCheck = integrationDto.externalCheck
    ? mapFromExternalCheckSchedule(integrationDto.externalCheck)
    : undefined;

  //parse the Recurrence pattern if set
  if (integrationDto.recurrencePattern) {
    integration.recurrencePattern = new RecurringPattern();
    integration.recurrencePattern.fromJSON(integrationDto.recurrencePattern);
  }

  return integration;
};

export const mapFromExternalCheckSchedule = (dto: ExternalCheckScheduleDTO): ExternalCheckSchedule => {
  const externalCheck = new ExternalCheckSchedule();
  externalCheck.checkKey = dto.checkKey;
  externalCheck.kind = dto.kind;
  externalCheck.skillId = dto.skillId;
  externalCheck.skillName = dto.skillName;
  externalCheck.publishedVersion = dto.publishedVersion;
  externalCheck.skillUrl = dto.skillUrl;
  externalCheck.stale = dto.stale;
  externalCheck.stateGeneratedAt = fromApiDateTimeOptional(dto.stateGeneratedAt);
  externalCheck.schedules =
    dto.schedules?.map((scheduleDto) => {
      const schedule = new ExternalCheckScheduleState();
      schedule.skillScheduleId = scheduleDto.skillScheduleId;
      schedule.name = scheduleDto.name;
      schedule.status = scheduleDto.status;
      schedule.nextDueAt = fromApiDateTimeOptional(scheduleDto.nextDueAt);
      schedule.lastFiredAt = fromApiDateTimeOptional(scheduleDto.lastFiredAt);
      schedule.lastOutcome = scheduleDto.lastOutcome;
      schedule.lastError = scheduleDto.lastError;
      schedule.consecutiveFailures = scheduleDto.consecutiveFailures;

      return schedule;
    }) ?? [];

  return externalCheck;
};

export const mapToIntegrations = (integrations: Integration[]): IntegrationDTO[] => {
  return integrations.map((i) => mapToIntegration(i));
};

export const mapToIntegration = (integration: Integration): IntegrationDTO => {
  const integrationDto = new IntegrationDTO();
  integrationDto.integrationId = integration.integrationId;
  integrationDto.integrationTypeId = integration.integrationTypeId;
  integrationDto.state = integration.state;
  integrationDto.config = JSON.stringify(integration.config);
  integrationDto.kpiId = integration.kpiId;
  integrationDto.kpi = mapToKPI(integration.kpi);
  integrationDto.ownerId = integration.ownerId;
  integrationDto.ownerRoleId = integration.ownerRoleId;
  integrationDto.templateId = integration.templateId;

  if (integration.recurrencePattern) {
    integrationDto.recurrencePattern = integration.recurrencePattern.toJSON();
  }

  return integrationDto;
};

export const mapFromIntegrationConnection = (dto: IntegrationConnectionDTO): IntegrationConnection => {
  const connection = new IntegrationConnection();
  connection.integrationConnectionId = dto.integrationConnectionId;
  connection.integrationConnectionTypeId = dto.integrationConnectionTypeId;
  connection.authData = dto.authData ? JSON.parse(dto.authData) : undefined;
  connection.config = dto.config ? JSON.parse(dto.config) : undefined;
  connection.isAuthorized = dto.isAuthorized ?? undefined;
  connection.lastVerified = fromApiDateTimeOptional(dto.lastVerified);
  connection.lastError = dto.lastError ?? undefined;
  connection.created = fromApiDateTimeRequired(dto.created);
  connection.modified = fromApiDateTimeOptional(dto.modified);
  connection.integrationTypeIds = dto.integrationTypeIds ?? [];

  return connection;
};

//Only the credential and the configuration are written by the API; the state is the API's.
export const mapToIntegrationConnection = (connection: IntegrationConnection): IntegrationConnectionDTO => {
  const dto = new IntegrationConnectionDTO();
  dto.integrationConnectionId = connection.integrationConnectionId;
  dto.integrationConnectionTypeId = connection.integrationConnectionTypeId;
  dto.authData = connection.authData ? JSON.stringify(connection.authData) : '';
  dto.config = connection.config ? JSON.stringify(connection.config) : '';

  return dto;
};

export const mapFromIntegrationTypes = (dto: IntegrationTypeDTO[], cache: GlobalDataCache): IntegrationType[] => {
  return dto.map((item) => mapFromIntegrationType(item, cache));
};

export const mapFromIntegrationType = (dto: IntegrationTypeDTO, cache: GlobalDataCache): IntegrationType => {
  const integrationType = new IntegrationType();
  integrationType.integrationTypeId = dto.integrationTypeId;
  integrationType.integrationCategories =
    dto.integrationCategories?.idList?.map((id) => Number(id) as IntegrationCategories) || [];
  integrationType.integrationGroupId = dto.integrationGroupId;
  integrationType.integrationCheckId = dto.integrationCheckId ?? IntegrationChecks.NotSet;
  integrationType.integrationConnectionTypeId = dto.integrationConnectionTypeId ?? IntegrationConnectionTypes.None;
  integrationType.authMode = dto.authMode;
  integrationType.integration = dto.integration ? mapFromIntegration(dto.integration, cache) : undefined;

  return integrationType;
};

export const mapFromIntegrationRunStats = (dtos: IntegrationRunStatsDTO[]): IntegrationRunStats[] => {
  return dtos.map((dto) => {
    const stats = new IntegrationRunStats();
    stats.integrationId = dto.integrationId;
    stats.integrationTypeId = dto.integrationTypeId;
    stats.integrationGroupId = dto.integrationGroupId;
    stats.kpiId = dto.kpiId;
    stats.acceptedCount = dto.acceptedCount;
    stats.rejectedCount = dto.rejectedCount;
    stats.deadCount = dto.deadCount;

    return stats;
  });
};

export const mapFromIntegrationGroup = (dto: IntegrationGroupDTO | undefined): IntegrationGroup | undefined => {
  if (!dto) return undefined;
  const group = new IntegrationGroup();
  group.integrationGroupId = dto.integrationGroupId;
  group.state = dto.state;
  group.autoConvertOnTrialEnd = dto.autoConvertOnTrialEnd;
  group.purchaseDate = fromApiDateTimeOptional(dto.purchaseDate);
  group.trialEndDate = fromApiDateTimeOptional(dto.trialEndDate);
  group.trialNotificationState = dto.trialNotificationState;

  return group;
};

export const mapFromIntegrationRun = (dto: IntegrationRunDTO, kpi: KPI, cache: GlobalDataCache): IntegrationRun => {
  const integrationRun = new IntegrationRun();
  integrationRun.integrationRunId = dto.integrationRunId;
  integrationRun.integrationId = dto.integrationId;
  integrationRun.status = dto.status;
  integrationRun.retryCount = dto.retryCount;
  integrationRun.created = fromApiDateTimeRequired(dto.created);
  integrationRun.dueAt = fromApiDateTimeRequired(dto.dueAt);
  integrationRun.lockedUntil = fromApiDateTimeOptional(dto.lockedUntil);
  integrationRun.lockOwner = dto.lockOwner;
  integrationRun.started = fromApiDateTimeOptional(dto.started);
  integrationRun.completed = fromApiDateTimeOptional(dto.completed);
  integrationRun.error = dto.error;
  integrationRun.kpiId = dto.kpiId;
  integrationRun.kpiDataId = dto.kpiDataId;
  integrationRun.data = dto.data ? mapFromKPIDataPoint(dto.data, kpi) : undefined;
  integrationRun.taskId = dto.taskId;
  integrationRun.task = dto.task ? mapFromTask(dto.task, cache) : undefined;

  return integrationRun;
};

export const mapFromIntegrationResult = (
  dto: IntegrationResultDTO,
  kpi: KPI,
  cache: GlobalDataCache,
): IntegrationResult => {
  const integrationResult = new IntegrationResult();
  integrationResult.integration = dto.integration ? mapFromIntegration(dto.integration, cache) : undefined;
  integrationResult.mode = dto.mode;
  integrationResult.run = dto.run ? mapFromIntegrationRun(dto.run, kpi, cache) : undefined;
  integrationResult.data = mapFromKPIDataPoint(dto.data, kpi);
  integrationResult.context = mapFromKPIDataContext(dto.context);
  integrationResult.taskForNotAcceptedResult = dto.taskForNotAcceptedResult
    ? mapFromTask(dto.taskForNotAcceptedResult, cache)
    : undefined;
  integrationResult.evidence = dto.evidence;

  return integrationResult;
};
